import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Bookmark, TrendingUp, BarChart3, MapPin, Building2, ArrowRight, X, DollarSign, MessageSquare, ChevronRight, Trash2, Eye, GitCompare, CheckSquare, Square, Sparkles, ArrowUpRight, ArrowDownRight, Minus, Bell, BellRing, Mail, MessageCircle, Clock, TrendingDown, Target, Home, Check } from 'lucide-react';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { wishlistService } from '../../services/wishlistService';
import { createPropertyInquiry } from '../../services/propertyInquiryService';
import { upsertWatchlistAlert, deactivateWatchlistAlert, triggerWatchlistAlertMonitor } from '../../services/watchlistAlertService';
import Icon from '../../components/AppIcon';





// ─── Helpers ────────────────────────────────────────────────────────────────

const estimateYield = (property) => {
  const price = property?.property_price || property?.price || 0;
  if (!price) return 0;
  const avgPricePerSqm = property?.commune?.average_price || 800000;
  const surface = property?.surface_area || 80;
  const estimatedMonthlyRent = avgPricePerSqm * surface * 0.007;
  const annualRent = estimatedMonthlyRent * 12;
  return price > 0 ? (annualRent / price) * 100 : 0;
};

const formatPrice = (price) => {
  if (!price) return '—';
  if (price >= 1_000_000_000) return `${(price / 1_000_000_000)?.toFixed(1)} Mrd FCFA`;
  if (price >= 1_000_000) return `${(price / 1_000_000)?.toFixed(0)} M FCFA`;
  return `${price?.toLocaleString('fr-FR')} FCFA`;
};

const getYieldColor = (y) => {
  if (y >= 8) return 'text-green-400';
  if (y >= 5) return 'text-brand-gold';
  return 'text-orange-400';
};

const getYieldBg = (y) => {
  if (y >= 8) return 'bg-green-400/10 border-green-400/30';
  if (y >= 5) return 'bg-brand-gold/10 border-brand-gold/30';
  return 'bg-orange-400/10 border-orange-400/30';
};

const getYieldTrend = (y) => {
  if (y >= 8) return { icon: ArrowUpRight, label: 'Excellent', color: 'text-green-400' };
  if (y >= 5) return { icon: Minus, label: 'Correct', color: 'text-brand-gold' };
  return { icon: ArrowDownRight, label: 'Faible', color: 'text-orange-400' };
};

// ─── Alert Storage Helpers ───────────────────────────────────────────────────

const ALERTS_KEY = 'kapital_watchlist_alerts';

const loadAllAlerts = () => {
  try {
    return JSON.parse(localStorage.getItem(ALERTS_KEY) || '{}');
  } catch {
    return {};
  }
};

const savePropertyAlert = (propertyId, alertData) => {
  const all = loadAllAlerts();
  all[propertyId] = alertData;
  localStorage.setItem(ALERTS_KEY, JSON.stringify(all));
};

const getPropertyAlert = (propertyId) => {
  return loadAllAlerts()?.[propertyId] || null;
};

const removePropertyAlert = (propertyId) => {
  const all = loadAllAlerts();
  delete all?.[propertyId];
  localStorage.setItem(ALERTS_KEY, JSON.stringify(all));
};

const DEFAULT_ALERT = {
  enabled: true,
  priceDrop: { active: false, threshold: 5 },
  yieldTarget: { active: false, target: 7 },
  similarProperties: { active: false },
  channels: { email: true, sms: false },
  frequency: 'immediate',
};

// ─── Alert Setup Modal ───────────────────────────────────────────────────────

const AlertSetupModal = ({ property, onClose, onSaved, userId }) => {
  const propertyId = property?.property_id || property?.id;
  const existingAlert = getPropertyAlert(propertyId);
  const [alert, setAlert] = useState(existingAlert || { ...DEFAULT_ALERT });
  const [saved, setSaved] = useState(false);

  const currentYield = estimateYield(property);

  const update = (path, value) => {
    setAlert(prev => {
      const next = { ...prev };
      const keys = path?.split('.');
      let obj = next;
      for (let i = 0; i < keys?.length - 1; i++) {
        obj[keys[i]] = { ...obj?.[keys?.[i]] };
        obj = obj?.[keys?.[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    const alertData = { ...alert, savedAt: new Date()?.toISOString() };
    savePropertyAlert(propertyId, alertData);
    // Persist to Supabase so the background monitor can pick it up
    if (userId && propertyId) {
      const currentPrice = property?.property_price || property?.price || null;
      await upsertWatchlistAlert(userId, propertyId, alert, currentPrice);
    }
    setSaved(true);
    setTimeout(() => { onSaved?.(); onClose(); }, 1400);
  };

  const handleRemove = async () => {
    removePropertyAlert(propertyId);
    // Deactivate in Supabase
    if (userId && propertyId) {
      await deactivateWatchlistAlert(userId, propertyId);
    }
    onSaved?.();
    onClose();
  };

  const frequencyOptions = [
    { value: 'immediate', label: 'Immédiat', desc: 'Dès que ça se produit' },
    { value: 'daily', label: 'Quotidien', desc: 'Résumé chaque jour' },
    { value: 'weekly', label: 'Hebdomadaire', desc: 'Résumé chaque semaine' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div className="bg-[#141a24] border border-white/15 rounded-3xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#141a24] z-10 rounded-t-3xl">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-brand-gold/15 rounded-xl flex items-center justify-center">
              <BellRing size={17} className="text-brand-gold" />
            </div>
            <div>
              <h3 className="text-white text-ios-title3 font-display font-display-semibold">Configurer l'alerte</h3>
              <p className="text-text-secondary text-ios-caption1 font-system mt-0.5 line-clamp-1 max-w-[220px]">
                {property?.property_title || property?.title || 'Propriété'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors flex-shrink-0">
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        {saved ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 bg-brand-gold/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-brand-gold" />
            </div>
            <h4 className="text-white text-ios-callout font-system font-body-semibold mb-1">Alerte sauvegardée !</h4>
            <p className="text-text-secondary text-ios-footnote font-system">Vous serez notifié selon vos préférences.</p>
          </div>
        ) : (
          <div className="p-6 space-y-5">

            {/* Master toggle */}
            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5">
              <div className="flex items-center space-x-3">
                <Bell size={16} className={alert?.enabled ? 'text-brand-gold' : 'text-text-secondary'} />
                <div>
                  <p className="text-white text-ios-callout font-system font-body-semibold">Alertes actives</p>
                  <p className="text-text-secondary text-ios-caption1 font-system">Activer/désactiver toutes les alertes</p>
                </div>
              </div>
              <button
                onClick={() => update('enabled', !alert?.enabled)}
                className={`relative w-12 h-6 rounded-full transition-all duration-300 ${alert?.enabled ? 'bg-brand-gold' : 'bg-white/15'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 ${alert?.enabled ? 'left-6' : 'left-0.5'}`} />
              </button>
            </div>

            <div className={`space-y-4 transition-opacity duration-200 ${!alert?.enabled ? 'opacity-40 pointer-events-none' : ''}`}>

              {/* Section: Trigger Conditions */}
              <div>
                <p className="text-text-secondary text-ios-caption1 font-system uppercase tracking-widest mb-3">Conditions de déclenchement</p>

                {/* Price Drop */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2.5">
                      <div className="w-8 h-8 bg-red-400/15 rounded-xl flex items-center justify-center">
                        <TrendingDown size={15} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-white text-ios-footnote font-system font-body-semibold">Baisse de prix</p>
                        <p className="text-text-secondary text-[10px] font-system">Alerte si le prix baisse</p>
                      </div>
                    </div>
                    <button
                      onClick={() => update('priceDrop.active', !alert?.priceDrop?.active)}
                      className={`relative w-10 h-5 rounded-full transition-all duration-300 ${alert?.priceDrop?.active ? 'bg-red-400' : 'bg-white/15'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 ${alert?.priceDrop?.active ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                  {alert?.priceDrop?.active && (
                    <div className="mt-3 pt-3 border-t border-white/8">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-text-secondary text-ios-caption1 font-system">Seuil de baisse</label>
                        <span className="text-red-400 text-ios-footnote font-system font-body-semibold">−{alert?.priceDrop?.threshold}%</span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={30}
                        step={1}
                        value={alert?.priceDrop?.threshold}
                        onChange={e => update('priceDrop.threshold', Number(e?.target?.value))}
                        className="w-full h-1.5 rounded-full appearance-none bg-white/15 accent-red-400 cursor-pointer"
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-white/30 text-[10px] font-system">1%</span>
                        <span className="text-white/30 text-[10px] font-system">30%</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {[3, 5, 10, 15]?.map(v => (
                          <button
                            key={v}
                            onClick={() => update('priceDrop.threshold', v)}
                            className={`flex-1 py-1.5 rounded-lg text-[10px] font-system font-body-semibold transition-all ${alert?.priceDrop?.threshold === v ? 'bg-red-400/20 border border-red-400/50 text-red-400' : 'bg-white/5 border border-white/10 text-text-secondary hover:border-white/20'}`}
                          >
                            −{v}%
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Yield Target */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2.5">
                      <div className="w-8 h-8 bg-green-400/15 rounded-xl flex items-center justify-center">
                        <Target size={15} className="text-green-400" />
                      </div>
                      <div>
                        <p className="text-white text-ios-footnote font-system font-body-semibold">Cible de rendement</p>
                        <p className="text-text-secondary text-[10px] font-system">
                          Rendement actuel : ~{currentYield?.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => update('yieldTarget.active', !alert?.yieldTarget?.active)}
                      className={`relative w-10 h-5 rounded-full transition-all duration-300 ${alert?.yieldTarget?.active ? 'bg-green-400' : 'bg-white/15'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 ${alert?.yieldTarget?.active ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                  {alert?.yieldTarget?.active && (
                    <div className="mt-3 pt-3 border-t border-white/8">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-text-secondary text-ios-caption1 font-system">Rendement cible</label>
                        <span className="text-green-400 text-ios-footnote font-system font-body-semibold">{alert?.yieldTarget?.target}%</span>
                      </div>
                      <input
                        type="range"
                        min={3}
                        max={15}
                        step={0.5}
                        value={alert?.yieldTarget?.target}
                        onChange={e => update('yieldTarget.target', Number(e?.target?.value))}
                        className="w-full h-1.5 rounded-full appearance-none bg-white/15 accent-green-400 cursor-pointer"
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-white/30 text-[10px] font-system">3%</span>
                        <span className="text-white/30 text-[10px] font-system">15%</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {[4, 6, 8, 10]?.map(v => (
                          <button
                            key={v}
                            onClick={() => update('yieldTarget.target', v)}
                            className={`flex-1 py-1.5 rounded-lg text-[10px] font-system font-body-semibold transition-all ${alert?.yieldTarget?.target === v ? 'bg-green-400/20 border border-green-400/50 text-green-400' : 'bg-white/5 border border-white/10 text-text-secondary hover:border-white/20'}`}
                          >
                            {v}%
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Similar Properties */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2.5">
                      <div className="w-8 h-8 bg-brand-gold/15 rounded-xl flex items-center justify-center">
                        <Home size={15} className="text-brand-gold" />
                      </div>
                      <div>
                        <p className="text-white text-ios-footnote font-system font-body-semibold">Biens similaires</p>
                        <p className="text-text-secondary text-[10px] font-system">Nouvelles propriétés comparables</p>
                      </div>
                    </div>
                    <button
                      onClick={() => update('similarProperties.active', !alert?.similarProperties?.active)}
                      className={`relative w-10 h-5 rounded-full transition-all duration-300 ${alert?.similarProperties?.active ? 'bg-brand-gold' : 'bg-white/15'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 ${alert?.similarProperties?.active ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Section: Notification Channels */}
              <div>
                <p className="text-text-secondary text-ios-caption1 font-system uppercase tracking-widest mb-3">Canaux de notification</p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Email */}
                  <button
                    onClick={() => update('channels.email', !alert?.channels?.email)}
                    className={`flex items-center space-x-3 p-4 rounded-2xl border transition-all ${alert?.channels?.email ? 'bg-blue-400/10 border-blue-400/40' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${alert?.channels?.email ? 'bg-blue-400/20' : 'bg-white/8'}`}>
                      <Mail size={15} className={alert?.channels?.email ? 'text-blue-400' : 'text-text-secondary'} />
                    </div>
                    <div className="text-left">
                      <p className={`text-ios-footnote font-system font-body-semibold ${alert?.channels?.email ? 'text-blue-400' : 'text-text-secondary'}`}>Email</p>
                      <p className="text-[10px] font-system text-white/30">{alert?.channels?.email ? 'Activé' : 'Désactivé'}</p>
                    </div>
                  </button>
                  {/* SMS */}
                  <button
                    onClick={() => update('channels.sms', !alert?.channels?.sms)}
                    className={`flex items-center space-x-3 p-4 rounded-2xl border transition-all ${alert?.channels?.sms ? 'bg-green-400/10 border-green-400/40' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${alert?.channels?.sms ? 'bg-green-400/20' : 'bg-white/8'}`}>
                      <MessageCircle size={15} className={alert?.channels?.sms ? 'text-green-400' : 'text-text-secondary'} />
                    </div>
                    <div className="text-left">
                      <p className={`text-ios-footnote font-system font-body-semibold ${alert?.channels?.sms ? 'text-green-400' : 'text-text-secondary'}`}>SMS</p>
                      <p className="text-[10px] font-system text-white/30">{alert?.channels?.sms ? 'Activé' : 'Désactivé'}</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Section: Frequency */}
              <div>
                <p className="text-text-secondary text-ios-caption1 font-system uppercase tracking-widest mb-3">Fréquence des notifications</p>
                <div className="space-y-2">
                  {frequencyOptions?.map(opt => (
                    <button
                      key={opt?.value}
                      onClick={() => update('frequency', opt?.value)}
                      className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border transition-all ${alert?.frequency === opt?.value ? 'bg-brand-gold/10 border-brand-gold/40' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${alert?.frequency === opt?.value ? 'bg-brand-gold/20' : 'bg-white/8'}`}>
                          <Clock size={14} className={alert?.frequency === opt?.value ? 'text-brand-gold' : 'text-text-secondary'} />
                        </div>
                        <div className="text-left">
                          <p className={`text-ios-footnote font-system font-body-semibold ${alert?.frequency === opt?.value ? 'text-brand-gold' : 'text-white'}`}>{opt?.label}</p>
                          <p className="text-text-secondary text-[10px] font-system">{opt?.desc}</p>
                        </div>
                      </div>
                      {alert?.frequency === opt?.value && (
                        <div className="w-5 h-5 bg-brand-gold rounded-full flex items-center justify-center flex-shrink-0">
                          <Check size={11} className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              {existingAlert && (
                <button
                  onClick={handleRemove}
                  className="flex items-center justify-center space-x-1.5 bg-red-400/10 hover:bg-red-400/20 border border-red-400/30 text-red-400 px-4 py-3 rounded-xl text-ios-footnote font-system font-body-semibold transition-all"
                >
                  <Trash2 size={14} />
                  <span>Supprimer</span>
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={!alert?.enabled && !existingAlert}
                className="flex-1 flex items-center justify-center space-x-2 bg-brand-gold hover:bg-brand-gold/90 text-white py-3 rounded-xl font-system font-body-semibold text-ios-callout transition-all disabled:opacity-50"
              >
                <BellRing size={15} />
                <span>Sauvegarder l'alerte</span>
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

// ─── One-Click Inquiry Modal ─────────────────────────────────────────────────

const InquiryModal = ({ property, onClose, onSuccess }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const { error } = await createPropertyInquiry({
      propertyId: property?.property_id || property?.id,
      message: form?.message || `Demande d'information pour la propriété : ${property?.property_title || property?.title}`,
      phone: form?.phone,
      email: form?.email,
      preferred_contact_time: null,
    });
    setLoading(false);
    if (!error) {
      setSent(true);
      setTimeout(() => { onSuccess?.(); onClose(); }, 1800);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#141a24] border border-white/15 rounded-3xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h3 className="text-white text-ios-title3 font-display font-display-semibold">Demande de contact</h3>
            <p className="text-text-secondary text-ios-footnote font-system mt-0.5 line-clamp-1">
              {property?.property_title || property?.title}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors">
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 bg-green-400/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckSquare size={28} className="text-green-400" />
            </div>
            <h4 className="text-white text-ios-callout font-system font-body-semibold mb-1">Demande envoyée !</h4>
            <p className="text-text-secondary text-ios-footnote font-system">Notre équipe vous contactera rapidement.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-text-secondary text-ios-caption1 font-system mb-1.5">Votre nom</label>
              <input
                type="text"
                value={form?.name}
                onChange={e => setForm(p => ({ ...p, name: e?.target?.value }))}
                placeholder="Jean Kouassi"
                className="w-full bg-white/8 border border-white/15 rounded-xl px-4 py-3 text-white text-ios-callout font-system placeholder-white/30 focus:outline-none focus:border-brand-gold/60 transition-all"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-text-secondary text-ios-caption1 font-system mb-1.5">Téléphone</label>
                <input
                  type="tel"
                  value={form?.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e?.target?.value }))}
                  placeholder="+225 07 00 00 00"
                  className="w-full bg-white/8 border border-white/15 rounded-xl px-4 py-3 text-white text-ios-callout font-system placeholder-white/30 focus:outline-none focus:border-brand-gold/60 transition-all"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-ios-caption1 font-system mb-1.5">Email</label>
                <input
                  type="email"
                  value={form?.email}
                  onChange={e => setForm(p => ({ ...p, email: e?.target?.value }))}
                  placeholder="jean@email.com"
                  className="w-full bg-white/8 border border-white/15 rounded-xl px-4 py-3 text-white text-ios-callout font-system placeholder-white/30 focus:outline-none focus:border-brand-gold/60 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-text-secondary text-ios-caption1 font-system mb-1.5">Message (optionnel)</label>
              <textarea
                value={form?.message}
                onChange={e => setForm(p => ({ ...p, message: e?.target?.value }))}
                placeholder="Je souhaite obtenir plus d'informations sur cet investissement..."
                rows={3}
                className="w-full bg-white/8 border border-white/15 rounded-xl px-4 py-3 text-white text-ios-callout font-system placeholder-white/30 focus:outline-none focus:border-brand-gold/60 transition-all resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2 bg-brand-gold hover:bg-brand-gold/90 text-white py-3.5 rounded-xl font-system font-body-semibold text-ios-callout transition-all disabled:opacity-60"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <MessageSquare size={16} />
              )}
              <span>{loading ? 'Envoi…' : 'Envoyer la demande'}</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Comparison Panel ────────────────────────────────────────────────────────

const ComparisonPanel = ({ items, onClose, onInquiry }) => {
  const metrics = [
    { label: 'Prix', key: 'price', format: (item) => formatPrice(item?.property_price || item?.price) },
    { label: 'Commune', key: 'commune', format: (item) => item?.property_commune || item?.commune?.name || '—' },
    { label: 'Type', key: 'type', format: (item) => item?.property_type || item?.type || '—' },
    { label: 'Surface', key: 'surface', format: (item) => item?.surface_area ? `${item?.surface_area} m²` : '—' },
    { label: 'Rendement brut est.', key: 'yield', format: (item) => {
      const y = estimateYield(item);
      return y > 0 ? `~${y?.toFixed(1)}%` : '—';
    }, highlight: true },
    { label: 'Loyer mensuel est.', key: 'rent', format: (item) => {
      const avgPricePerSqm = item?.commune?.average_price || 800000;
      const surface = item?.surface_area || 80;
      const rent = avgPricePerSqm * surface * 0.007;
      return formatPrice(Math.round(rent));
    }},
    { label: 'Marché', key: 'market', format: (item) => item?.market_type === 'rental' ? 'Location' : 'Vente' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-[#0f1117] border border-white/15 rounded-t-3xl md:rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <GitCompare size={18} className="text-brand-gold" />
            <h3 className="text-white text-ios-title3 font-display font-display-semibold">Comparaison côte à côte</h3>
            <span className="bg-brand-gold/20 text-brand-gold text-ios-caption1 font-system font-body-semibold px-2 py-0.5 rounded-full">
              {items?.length} biens
            </span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors">
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full">
            {/* Property headers */}
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-6 py-4 text-text-secondary text-ios-caption1 font-system uppercase tracking-widest w-36">Critère</th>
                {items?.map((item, idx) => {
                  const y = estimateYield(item);
                  const trend = getYieldTrend(y);
                  return (
                    <th key={item?.id || idx} className="px-4 py-4 text-center min-w-[180px]">
                      <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                        <div className="w-10 h-10 bg-brand-gold/15 rounded-xl flex items-center justify-center mx-auto mb-2">
                          <Building2 size={18} className="text-brand-gold" />
                        </div>
                        <p className="text-white text-ios-footnote font-system font-body-semibold line-clamp-2 mb-1">
                          {item?.property_title || item?.title || 'Propriété'}
                        </p>
                        <div className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full border text-[10px] font-system font-body-semibold ${getYieldBg(y)}`}>
                          <trend.icon size={10} className={trend?.color} />
                          <span className={trend?.color}>{trend?.label}</span>
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {metrics?.map((metric, mIdx) => (
                <tr key={metric?.key} className={`border-b border-white/5 ${mIdx % 2 === 0 ? 'bg-white/2' : ''}`}>
                  <td className="px-6 py-4 text-text-secondary text-ios-footnote font-system">{metric?.label}</td>
                  {items?.map((item, idx) => {
                    const val = metric?.format(item);
                    const isYield = metric?.highlight;
                    const y = isYield ? estimateYield(item) : null;
                    return (
                      <td key={idx} className="px-4 py-4 text-center">
                        <span className={`text-ios-callout font-system font-body-semibold ${isYield ? getYieldColor(y) : 'text-white'}`}>
                          {val}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Actions row */}
              <tr>
                <td className="px-6 py-5 text-text-secondary text-ios-footnote font-system">Actions</td>
                {items?.map((item, idx) => (
                  <td key={idx} className="px-4 py-5 text-center">
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => onInquiry(item)}
                        className="flex items-center justify-center space-x-1.5 bg-brand-gold hover:bg-brand-gold/90 text-white px-4 py-2 rounded-xl text-ios-caption1 font-system font-body-semibold transition-all"
                      >
                        <MessageSquare size={12} />
                        <span>Contacter</span>
                      </button>
                      <Link
                        to={`/individual-property-page?id=${item?.property_id || item?.id}`}
                        className="flex items-center justify-center space-x-1.5 bg-white/8 hover:bg-white/15 border border-white/15 text-white px-4 py-2 rounded-xl text-ios-caption1 font-system font-body-medium transition-all"
                      >
                        <Eye size={12} />
                        <span>Voir</span>
                      </Link>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Watchlist Card ──────────────────────────────────────────────────────────

const WatchlistCard = ({ item, isSelected, onToggleSelect, onRemove, onInquiry, onAlert }) => {
  const yieldPct = estimateYield(item);
  const trend = getYieldTrend(yieldPct);
  const TrendIcon = trend?.icon;
  const estimatedRent = Math.round(
    (item?.commune?.average_price || 800000) * (item?.surface_area || 80) * 0.007
  );
  const propertyId = item?.property_id || item?.id;
  const hasAlert = !!getPropertyAlert(propertyId);

  return (
    <div className={`relative bg-white/5 backdrop-blur-sm border rounded-2xl overflow-hidden transition-all duration-300 hover:bg-white/8 group ${
      isSelected ? 'border-brand-gold/60 shadow-brand-gold/15 shadow-lg' : 'border-white/10 hover:border-white/20'
    }`}>
      {/* Select checkbox */}
      <button
        onClick={() => onToggleSelect(item)}
        className="absolute top-3 left-3 z-10 w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all hover:border-brand-gold/60"
      >
        {isSelected
          ? <CheckSquare size={14} className="text-brand-gold" />
          : <Square size={14} className="text-white/50" />
        }
      </button>

      {/* Alert bell button */}
      <button
        onClick={() => onAlert(item)}
        title="Configurer une alerte"
        className={`absolute top-3 right-11 z-10 w-7 h-7 rounded-lg backdrop-blur-sm border flex items-center justify-center transition-all ${
          hasAlert
            ? 'bg-brand-gold/20 border-brand-gold/50 hover:bg-brand-gold/30' :'bg-black/50 border-white/20 hover:border-brand-gold/50'
        }`}
      >
        {hasAlert
          ? <BellRing size={13} className="text-brand-gold" />
          : <Bell size={13} className="text-white/50" />
        }
      </button>

      {/* Remove */}
      <button
        onClick={() => onRemove(item)}
        className="absolute top-3 right-3 z-10 w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all hover:border-red-400/60 hover:bg-red-400/10"
      >
        <Trash2 size={13} className="text-white/50 hover:text-red-400 transition-colors" />
      </button>

      {/* Image */}
      <div className="h-40 bg-gradient-to-br from-brand-earth/20 to-brand-gold/10 relative overflow-hidden">
        {item?.property_image ? (
          <img
            src={item?.property_image}
            alt={`Vue de la propriété ${item?.property_title || 'immobilière'} à ${item?.property_commune || 'Abidjan'}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 size={36} className="text-white/20" />
          </div>
        )}
        {/* Yield overlay */}
        <div className={`absolute bottom-3 right-3 flex items-center space-x-1.5 px-2.5 py-1.5 rounded-xl border backdrop-blur-sm ${getYieldBg(yieldPct)}`}>
          <BarChart3 size={12} className={getYieldColor(yieldPct)} />
          <span className={`text-[11px] font-system font-body-semibold ${getYieldColor(yieldPct)}`}>
            ~{yieldPct?.toFixed(1)}%
          </span>
        </div>
        {/* Alert active badge */}
        {hasAlert && (
          <div className="absolute bottom-3 left-3 flex items-center space-x-1 bg-brand-gold/20 border border-brand-gold/40 backdrop-blur-sm px-2 py-1 rounded-lg">
            <BellRing size={10} className="text-brand-gold" />
            <span className="text-brand-gold text-[9px] font-system font-body-semibold">Alerte active</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-white text-ios-callout font-system font-body-semibold leading-snug mb-1.5 line-clamp-2">
          {item?.property_title || 'Propriété sans titre'}
        </h3>
        <div className="flex items-center space-x-1.5 mb-4">
          <MapPin size={12} className="text-text-secondary flex-shrink-0" />
          <span className="text-text-secondary text-ios-caption1 font-system">{item?.property_commune || '—'}</span>
          {item?.property_type && (
            <>
              <span className="text-white/20">·</span>
              <span className="text-text-secondary text-ios-caption1 font-system">{item?.property_type}</span>
            </>
          )}
        </div>

        {/* Performance metrics */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-brand-gold text-ios-footnote font-system font-body-semibold">{formatPrice(item?.property_price)}</p>
            <p className="text-text-secondary text-[10px] font-system mt-0.5">Prix</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center space-x-1">
              <TrendIcon size={11} className={trend?.color} />
              <p className={`text-ios-footnote font-system font-body-semibold ${trend?.color}`}>{trend?.label}</p>
            </div>
            <p className="text-text-secondary text-[10px] font-system mt-0.5">Performance</p>
          </div>
        </div>

        {/* Estimated rent */}
        <div className="bg-green-400/8 border border-green-400/20 rounded-xl px-3 py-2 mb-4 flex items-center justify-between">
          <span className="text-text-secondary text-ios-caption1 font-system">Loyer mensuel est.</span>
          <span className="text-green-400 text-ios-footnote font-system font-body-semibold">{formatPrice(estimatedRent)}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onInquiry(item)}
            className="flex-1 flex items-center justify-center space-x-1.5 bg-brand-gold hover:bg-brand-gold/90 text-white py-2.5 rounded-xl text-ios-caption1 font-system font-body-semibold transition-all"
          >
            <MessageSquare size={13} />
            <span>Contacter</span>
          </button>
          <button
            onClick={() => onAlert(item)}
            className={`flex items-center justify-center w-10 h-10 border rounded-xl transition-all ${
              hasAlert
                ? 'bg-brand-gold/15 border-brand-gold/40 hover:bg-brand-gold/25' :'bg-white/8 hover:bg-white/15 border-white/15'
            }`}
            title="Configurer une alerte"
          >
            {hasAlert
              ? <BellRing size={14} className="text-brand-gold" />
              : <Bell size={14} className="text-white/70" />
            }
          </button>
          <Link
            to={`/individual-property-page?id=${item?.property_id}`}
            className="flex items-center justify-center w-10 h-10 bg-white/8 hover:bg-white/15 border border-white/15 rounded-xl transition-all"
          >
            <Eye size={14} className="text-white/70" />
          </Link>
          <Link
            to={`/roi-calculator-tool?propertyId=${item?.property_id}`}
            className="flex items-center justify-center w-10 h-10 bg-white/8 hover:bg-white/15 border border-white/15 rounded-xl transition-all"
          >
            <BarChart3 size={14} className="text-white/70" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const InvestorWatchlist = () => {
  const { user } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [inquiryTarget, setInquiryTarget] = useState(null);
  const [alertTarget, setAlertTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortBy, setSortBy] = useState('yield');
  const [alertsVersion, setAlertsVersion] = useState(0);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadWatchlist = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    const { data } = await wishlistService?.getWishlist(user?.id);
    setWatchlist(data || []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { loadWatchlist(); }, [loadWatchlist]);

  const handleRemove = async (item) => {
    const { error } = await wishlistService?.removeFromWishlist(user?.id, item?.property_id);
    if (!error) {
      setWatchlist(prev => prev?.filter(w => w?.property_id !== item?.property_id));
      setSelected(prev => prev?.filter(s => s?.property_id !== item?.property_id));
      showToast('Propriété retirée de la watchlist');
    }
  };

  const handleToggleSelect = (item) => {
    setSelected(prev => {
      const exists = prev?.find(s => s?.property_id === item?.property_id);
      if (exists) return prev?.filter(s => s?.property_id !== item?.property_id);
      if (prev?.length >= 4) {
        showToast('Maximum 4 propriétés pour la comparaison', 'warning');
        return prev;
      }
      return [...prev, item];
    });
  };

  const handleAlertSaved = () => {
    setAlertsVersion(v => v + 1);
    showToast('Alerte mise à jour avec succès !');
    // Trigger background monitor to check conditions immediately
    triggerWatchlistAlertMonitor();
  };

  const sortedWatchlist = [...watchlist]?.sort((a, b) => {
    if (sortBy === 'yield') return estimateYield(b) - estimateYield(a);
    if (sortBy === 'price_asc') return (a?.property_price || 0) - (b?.property_price || 0);
    if (sortBy === 'price_desc') return (b?.property_price || 0) - (a?.property_price || 0);
    if (sortBy === 'recent') return new Date(b?.created_at) - new Date(a?.created_at);
    return 0;
  });

  // Portfolio stats
  const totalValue = watchlist?.reduce((sum, w) => sum + (w?.property_price || 0), 0);
  const avgYield = watchlist?.length
    ? watchlist?.reduce((sum, w) => sum + estimateYield(w), 0) / watchlist?.length
    : 0;
  const topYield = watchlist?.length
    ? Math.max(...watchlist?.map(w => estimateYield(w)))
    : 0;
  const activeAlertsCount = watchlist?.filter(w => !!getPropertyAlert(w?.property_id || w?.id))?.length;

  return (
    <>
      <Helmet>
        <title>Ma Watchlist Investisseur — KapitalStates</title>
        <meta name="description" content="Suivez vos propriétés bookmarkées, comparez-les côte à côte et contactez en un clic." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#0f1117] via-[#141a24] to-[#0a0f18]">
        <Header />

        {/* Toast */}
        {toast && (
          <div className={`fixed top-20 right-6 z-50 flex items-center space-x-2 px-4 py-3 rounded-2xl shadow-xl border text-ios-footnote font-system font-body-medium transition-all ${
            toast?.type === 'warning' ? 'bg-orange-400/15 border-orange-400/30 text-orange-300' : 'bg-green-400/15 border-green-400/30 text-green-300'
          }`}>
            <CheckSquare size={14} />
            <span>{toast?.msg}</span>
          </div>
        )}

        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/3 w-80 h-80 bg-brand-gold/8 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-brand-earth/8 rounded-full blur-3xl" />
          </div>
          <div className="relative z-10 max-w-6xl mx-auto px-6 pt-12 pb-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/30 rounded-full px-4 py-1.5 mb-4">
                  <Bookmark size={13} className="text-brand-gold" />
                  <span className="text-brand-gold text-ios-caption1 font-system font-body-semibold tracking-widest uppercase">Watchlist Investisseur</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-display font-display-semibold text-white leading-tight mb-2">
                  Mes propriétés <span className="text-brand-gold">sauvegardées</span>
                </h1>
                <p className="text-text-secondary text-ios-callout font-system max-w-lg">
                  Suivez vos opportunités, comparez les rendements et contactez en un clic.
                </p>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                {selected?.length >= 2 && (
                  <button
                    onClick={() => setShowComparison(true)}
                    className="flex items-center space-x-2 bg-brand-gold hover:bg-brand-gold/90 text-white px-5 py-2.5 rounded-xl font-system font-body-semibold text-ios-callout transition-all shadow-lg"
                  >
                    <GitCompare size={16} />
                    <span>Comparer ({selected?.length})</span>
                  </button>
                )}
                <Link to="/investor-mode" className="flex items-center space-x-2 bg-white/8 hover:bg-white/15 border border-white/15 text-white px-5 py-2.5 rounded-xl font-system font-body-medium text-ios-callout transition-all">
                  <Sparkles size={15} />
                  <span>Mode Investisseur</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 pb-16">

          {/* Portfolio Stats */}
          {watchlist?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Propriétés suivies', value: watchlist?.length, suffix: '', icon: Bookmark, color: 'text-brand-gold', bg: 'bg-brand-gold/10 border-brand-gold/20' },
                { label: 'Valeur totale du portefeuille', value: formatPrice(totalValue), suffix: '', icon: DollarSign, color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/20' },
                { label: 'Rendement moyen', value: avgYield?.toFixed(1), suffix: '%', icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/20' },
                { label: 'Alertes actives', value: activeAlertsCount, suffix: '', icon: BellRing, color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/20' },
              ]?.map(({ label, value, suffix, icon: StatIcon, color, bg }, i) => {
                const Icon = StatIcon;
                return (
                  <div key={i} className={`bg-white/5 border rounded-2xl p-5 ${bg}`}>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${bg}`}>
                      <Icon size={18} className={color} />
                    </div>
                    <p className={`text-xl font-display font-display-semibold ${color}`}>{value}{suffix}</p>
                    <p className="text-text-secondary text-ios-caption1 font-system mt-0.5">{label}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Toolbar */}
          {watchlist?.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                {selected?.length > 0 && (
                  <span className="text-brand-gold text-ios-footnote font-system font-body-medium">
                    {selected?.length} sélectionné{selected?.length > 1 ? 's' : ''}
                  </span>
                )}
                {selected?.length > 0 && (
                  <button
                    onClick={() => setSelected([])}
                    className="text-text-secondary text-ios-caption1 font-system hover:text-white transition-colors"
                  >
                    Tout désélectionner
                  </button>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-text-secondary text-ios-caption1 font-system">Trier par :</span>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e?.target?.value)}
                  className="bg-white/8 border border-white/15 rounded-xl px-3 py-2 text-white text-ios-footnote font-system focus:outline-none focus:border-brand-gold/60 transition-all appearance-none"
                >
                  <option value="yield" className="bg-[#141a24]">Rendement</option>
                  <option value="price_asc" className="bg-[#141a24]">Prix croissant</option>
                  <option value="price_desc" className="bg-[#141a24]">Prix décroissant</option>
                  <option value="recent" className="bg-[#141a24]">Plus récent</option>
                </select>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-10 h-10 border-2 border-brand-gold/30 border-t-brand-gold rounded-full animate-spin mb-4" />
              <p className="text-text-secondary text-ios-callout font-system">Chargement de votre watchlist…</p>
            </div>
          ) : !user ? (
            <div className="bg-white/5 border border-white/10 rounded-3xl p-12 text-center">
              <div className="w-16 h-16 bg-brand-gold/15 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Bookmark size={28} className="text-brand-gold" />
              </div>
              <h3 className="text-white text-ios-title3 font-display font-display-semibold mb-2">Connectez-vous pour voir votre watchlist</h3>
              <p className="text-text-secondary text-ios-callout font-system mb-6 max-w-sm mx-auto">
                Sauvegardez vos propriétés préférées et suivez leurs performances depuis votre espace personnel.
              </p>
              <Link to="/auth" className="inline-flex items-center space-x-2 bg-brand-gold hover:bg-brand-gold/90 text-white px-6 py-3 rounded-xl font-system font-body-semibold text-ios-callout transition-all">
                <span>Se connecter</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          ) : watchlist?.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-3xl p-12 text-center">
              <div className="w-16 h-16 bg-brand-gold/15 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Bookmark size={28} className="text-brand-gold" />
              </div>
              <h3 className="text-white text-ios-title3 font-display font-display-semibold mb-2">Watchlist vide</h3>
              <p className="text-text-secondary text-ios-callout font-system mb-6 max-w-sm mx-auto">
                Ajoutez des propriétés à votre watchlist depuis la recherche ou le mode investisseur pour les suivre ici.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link to="/investor-mode" className="inline-flex items-center space-x-2 bg-brand-gold hover:bg-brand-gold/90 text-white px-5 py-2.5 rounded-xl font-system font-body-semibold text-ios-callout transition-all">
                  <Sparkles size={15} />
                  <span>Mode Investisseur</span>
                </Link>
                <Link to="/property-search-listings" className="inline-flex items-center space-x-2 bg-white/8 hover:bg-white/15 border border-white/15 text-white px-5 py-2.5 rounded-xl font-system font-body-medium text-ios-callout transition-all">
                  <span>Parcourir les biens</span>
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Comparison hint */}
              {selected?.length === 1 && (
                <div className="bg-brand-gold/8 border border-brand-gold/20 rounded-2xl px-5 py-3 mb-5 flex items-center space-x-3">
                  <GitCompare size={16} className="text-brand-gold flex-shrink-0" />
                  <p className="text-brand-gold text-ios-footnote font-system">
                    Sélectionnez au moins 2 propriétés pour activer la comparaison côte à côte.
                  </p>
                </div>
              )}

              {/* Alert info banner */}
              {activeAlertsCount === 0 && watchlist?.length > 0 && (
                <div className="bg-white/4 border border-white/10 rounded-2xl px-5 py-3.5 mb-5 flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <Bell size={16} className="text-text-secondary flex-shrink-0" />
                    <p className="text-text-secondary text-ios-footnote font-system">
                      Configurez des alertes sur vos propriétés pour être notifié des baisses de prix, cibles de rendement ou nouveaux biens similaires.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {sortedWatchlist?.map((item) => (
                  <WatchlistCard
                    key={`${item?.id || item?.property_id}-${alertsVersion}`}
                    item={item}
                    isSelected={!!selected?.find(s => s?.property_id === item?.property_id)}
                    onToggleSelect={handleToggleSelect}
                    onRemove={handleRemove}
                    onInquiry={setInquiryTarget}
                    onAlert={setAlertTarget}
                  />
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-10 bg-gradient-to-r from-brand-gold/10 to-brand-earth/10 border border-brand-gold/20 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h4 className="text-white text-ios-callout font-system font-body-semibold mb-1">Affinez votre analyse ROI</h4>
                  <p className="text-text-secondary text-ios-footnote font-system">Calculez le retour sur investissement précis de chaque bien.</p>
                </div>
                <Link to="/roi-calculator-tool">
                  <button className="flex items-center space-x-2 bg-brand-gold/20 hover:bg-brand-gold/30 border border-brand-gold/40 text-brand-gold px-5 py-2.5 rounded-xl text-ios-footnote font-system font-body-semibold transition-all whitespace-nowrap">
                    <BarChart3 size={14} />
                    <span>Calculateur ROI</span>
                    <ChevronRight size={14} />
                  </button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Modals */}
      {showComparison && selected?.length >= 2 && (
        <ComparisonPanel
          items={selected}
          onClose={() => setShowComparison(false)}
          onInquiry={(item) => { setShowComparison(false); setInquiryTarget(item); }}
        />
      )}
      {inquiryTarget && (
        <InquiryModal
          property={inquiryTarget}
          onClose={() => setInquiryTarget(null)}
          onSuccess={() => showToast('Demande envoyée avec succès !')}
        />
      )}
      {alertTarget && (
        <AlertSetupModal
          property={alertTarget}
          onClose={() => setAlertTarget(null)}
          onSaved={handleAlertSaved}
          userId={user?.id}
        />
      )}
    </>
  );
};

export default InvestorWatchlist;
